import { Injectable } from '@angular/core';
import { Observable, timer, of, Subject, merge, BehaviorSubject } from 'rxjs';
import {
  shareReplay,
  scan,
  distinctUntilChanged,
  map,
  tap,
  switchMap
} from 'rxjs/operators';
import { Task, TaskDTO, convertTaskDTO } from './task.type';
import { environment } from 'environments/environment';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { TokenService } from 'app/authentication/token.service';

const TASK_LONG_POLLING_INTERVAL = 900000; // once every 15min
const TASK_SHORT_POLLING_INTERVAL = 10000; // once every 10s

@Injectable({ providedIn: 'root' })
export class TasksService {
  activeTasks$: Observable<Task[]>;
  private addTaskSubject: Subject<Task> = new Subject();
  private removeTaskSubject: Subject<number> = new Subject();
  private pollingIntervalSubject: Subject<number> = new BehaviorSubject(
    TASK_SHORT_POLLING_INTERVAL
  );
  constructor(private http: HttpClient, private tokenService: TokenService) {
    this.init();
  }

  getAllTasks(): Observable<Task[]> {
    return this.http
      .get<{ data: TaskDTO[] }>('/PosServer/rest/manager/v1/tasks')
      .pipe(
        map(response => response.data.map(dto => convertTaskDTO(dto))),
        shareReplay(1)
      );
  }
  createTask(task: Task) {
    this.addTaskSubject.next(task);
    this.pollingIntervalSubject.next(TASK_SHORT_POLLING_INTERVAL);
  }

  removeTask(id: number): void {
    this.http
      .post(`/PosServer/rest/manager/v1/tasks/${id}/acknowledge`, {})
      .subscribe(() => this.removeTaskSubject.next(id));
  }

  getResult(id: number): void {
    this.http
      .get(`/PosServer/rest/manager/v1/tasks/${id}/result`, {
        responseType: 'blob',
        observe: 'response'
      })
      .subscribe(({ body, headers }: HttpResponse<Blob>) => {
        const contentDisposition = headers.get('content-disposition');
        const filename = contentDisposition
          ? contentDisposition.replace(/attachment; filename=(.*)/, '$1')
          : 'result.txt';
        const type = body.type;
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(
          new Blob([body], { type })
        );
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.parentNode.removeChild(downloadLink);
      });
  }

  private getAllActiveTasks(): Observable<Task[]> {
    const params = new HttpParams({ fromObject: { acknowledged: 'false' } });
    return this.http
      .get<{ data: TaskDTO[] }>('/PosServer/rest/manager/v1/tasks', {
        params
      })
      .pipe(
        map(response => response.data.map(dto => convertTaskDTO(dto))),
        tap(tasks => {
          const hasActiveTasks = tasks.some(task => task.status === 'pending');
          this.pollingIntervalSubject.next(
            hasActiveTasks
              ? TASK_SHORT_POLLING_INTERVAL
              : TASK_LONG_POLLING_INTERVAL
          );
        }),
        shareReplay(1)
      );
  }

  private init() {
    this.tokenService.tokenChanged$.subscribe(() =>
      this.pollingIntervalSubject.next(TASK_SHORT_POLLING_INTERVAL)
    );
    const polledTasks$ = environment.tasks
      ? this.pollingIntervalSubject.pipe(
          distinctUntilChanged(),
          switchMap(interval =>
            timer(
              interval === TASK_SHORT_POLLING_INTERVAL ? 0 : interval,
              interval
            )
          ),
          switchMap(() => this.getAllActiveTasks())
        )
      : of([]);
    this.activeTasks$ = merge(
      polledTasks$,
      this.addTaskSubject,
      this.removeTaskSubject
    ).pipe(
      scan((previous: Task[], current) => {
        if (typeof current === 'number') {
          return previous.filter(task => task.id !== current);
        }
        if (Array.isArray(current)) {
          if (!previous || previous.length !== current.length) {
            return current;
          }
          const same = previous.every(
            (prevTask, i) =>
              prevTask.id === current[i].id &&
              prevTask.status === current[i].status
          );
          return same ? previous : current;
        } else {
          return previous ? previous.concat(current) : [current];
        }
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }
}
